import { useDebounce, useJobillaOrgId } from '@/hooks'
import {
	hubSpotLinkShowQueryKey,
	useHubSpotLinkDestroy,
	useHubSpotLinkSearch,
	useHubSpotLinkShow,
	useHubSpotLinkStore,
} from '@jobilla/nexus-sdk'
import type { HubSpotLinkSearchResponse, HubSpotLinkShowResponse } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useRef, useState } from 'react'
import { Button, ConfirmationDialog, ConfirmationDialogRef } from '../../../../rapid-ui'
import { SettingsSubheading } from '../../shared'

type HubSpotCompany = HubSpotLinkSearchResponse[number]

const HUBSPOT_PORTAL_ID = '7914397'

function responseMessage(error: unknown, fallback: string): string {
	return (error as AxiosError<{ message: string }>)?.response?.data?.message ?? fallback
}

export function HubspotLinkSection() {
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search, 300)
	const [error, setError] = useState<string | null>(null)

	const linkQuery = useHubSpotLinkShow<HubSpotLinkShowResponse | null>(organisationId, {
		// The endpoint answers 204 when nothing is linked, so the body is empty
		// rather than an object.
		select: (data) => (data && typeof data === 'object' ? data : null),
	})

	const searchQuery = useHubSpotLinkSearch(
		organisationId,
		{ q: debouncedSearch },
		{ enabled: debouncedSearch.length >= 2 },
	)

	const storeMutation = useHubSpotLinkStore({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: hubSpotLinkShowQueryKey(organisationId) })
			setSearch('')
			setError(null)
		},
	})

	const destroyMutation = useHubSpotLinkDestroy({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: hubSpotLinkShowQueryKey(organisationId) })
		},
	})

	const handleLink = async (company: HubSpotCompany) => {
		try {
			await confirmRef.current?.confirm({
				title: 'Link HubSpot Company',
				message: `Link "${company.name}" (${company.id}) to this organisation?`,
				confirmText: 'Link',
			})
		} catch {
			return
		}

		setError(null)

		try {
			await storeMutation.mutateAsync({
				organisation: organisationId,
				data: { hubspotCompanyId: company.id },
			})
		} catch (err) {
			const axiosError = err as AxiosError<{ message: string }>
			if (axiosError.response?.status === 409) {
				const conflictMessage = axiosError.response.data?.message
					?? 'This company is already linked to another organisation.'
				try {
					await confirmRef.current?.confirm({
						title: 'Company Already Linked',
						message: `${conflictMessage} Do you want to move it to this organisation?`,
						confirmText: 'Move link',
					})
				} catch {
					return
				}

				try {
					await storeMutation.mutateAsync({
						organisation: organisationId,
						data: { hubspotCompanyId: company.id, force: true },
					})
				} catch (forceErr) {
					const forceAxiosError = forceErr as AxiosError<{ message: string }>
					setError(forceAxiosError.response?.data?.message ?? 'Failed to link company.')
				}
			} else {
				setError(axiosError.response?.data?.message ?? 'Failed to link company.')
			}
		}
	}

	const handleUnlink = async () => {
		try {
			await confirmRef.current?.confirm({
				title: 'Unlink HubSpot Company',
				message: 'This will remove the connection between this organisation and HubSpot. Are you sure?',
				confirmText: 'Unlink',
			})
		} catch {
			return
		}

		try {
			await destroyMutation.mutateAsync({ organisation: organisationId })
		} catch (err) {
			const axiosError = err as AxiosError<{ message: string }>
			setError(axiosError.response?.data?.message ?? 'Failed to unlink company.')
		}
	}

	const currentLink = linkQuery.data

	return (
		<>
			<SettingsSubheading>HubSpot</SettingsSubheading>
			<div className="max-w-[600px]">
				{linkQuery.isLoading
					? <p className="text-sm text-gray-500 mt-4">Loading...</p>
					: linkQuery.isError
					// Falling through to UnlinkedState would claim the organisation has no HubSpot
					// connection when the lookup merely failed, inviting a second, conflicting link.
					? (
						<div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
							{responseMessage(
								linkQuery.error,
								'Could not load the HubSpot connection.',
							)} Linking is disabled until it loads, so an existing connection is not replaced by mistake.
						</div>
					)
					: currentLink
					? <LinkedState link={currentLink} onUnlink={handleUnlink} isUnlinking={destroyMutation.isPending} />
					: (
						<UnlinkedState
							search={search}
							onSearchChange={setSearch}
							results={searchQuery.data ?? []}
							isSearching={searchQuery.isFetching}
							searchError={searchQuery.isError
								? responseMessage(
									searchQuery.error,
									'Company search is unavailable right now. Please try again in a few minutes.',
								)
								: null}
							onSelect={handleLink}
							isLinking={storeMutation.isPending}
						/>
					)}

				{error && (
					<div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
						{error}
					</div>
				)}
			</div>

			<ConfirmationDialog ref={confirmRef} />
		</>
	)
}

type LinkedStateProps = {
	link: HubSpotLinkShowResponse
	onUnlink: () => void
	isUnlinking: boolean
}

function LinkedState({ link, onUnlink, isUnlinking }: LinkedStateProps) {
	const hubspotUrl = `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/company/${link.hubspotCompanyId}`

	return (
		<div className="mt-4 rounded-lg border border-gray-200 p-4">
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					<p className="text-sm font-medium text-gray-900">
						{link.company?.name ?? 'Unknown company'}
					</p>
					<p className="text-xs text-gray-500 mt-0.5">
						ID: {link.hubspotCompanyId}
						{link.company?.domain && ` · ${link.company.domain}`}
					</p>
					<a
						href={hubspotUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-brand-600 hover:text-brand-700 mt-1 inline-block"
					>
						View in HubSpot &rarr;
					</a>
				</div>
				<Button
					size="tiny"
					type="destructive"
					onClick={onUnlink}
					disabled={isUnlinking}
				>
					{isUnlinking ? 'Unlinking...' : 'Unlink'}
				</Button>
			</div>
		</div>
	)
}

type UnlinkedStateProps = {
	search: string
	onSearchChange: (value: string) => void
	results: HubSpotLinkSearchResponse
	isSearching: boolean
	searchError: string | null
	onSelect: (company: HubSpotCompany) => void
	isLinking: boolean
}

function UnlinkedState(
	{ search, onSearchChange, results, isSearching, searchError, onSelect, isLinking }: UnlinkedStateProps,
) {
	return (
		<div className="mt-4">
			<p className="text-sm text-gray-500 mb-3">Not linked to HubSpot</p>
			<input
				type="text"
				placeholder="Search HubSpot companies..."
				value={search}
				onChange={(e) => onSearchChange(e.target.value)}
				className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none"
				disabled={isLinking}
			/>

			{isSearching && <p className="text-sm text-gray-400 mt-2">Searching...</p>}

			{!isSearching && searchError !== null && (
				<p className="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-700">{searchError}</p>
			)}

			{!isSearching && searchError === null && search.length >= 2 && results.length === 0 && (
				<p className="text-sm text-gray-400 mt-2">No results</p>
			)}

			{results.length > 0 && (
				<ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
					{results.map((company) => (
						<li key={company.id}>
							<button
								type="button"
								className="w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
								onClick={() => onSelect(company)}
								disabled={isLinking}
							>
								<p className="text-sm font-medium text-gray-900">
									{company.name ?? 'Unnamed'}
								</p>
								<p className="text-xs text-gray-500 mt-0.5">
									{['ID: ' + company.id, company.domain, company.city, company.country]
										.filter(Boolean)
										.join(' · ')}
								</p>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
